import { useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useForm, Controller } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Divider,
  Stack,
  Snackbar,
} from "@mui/material";
import { Save as SaveIcon } from "@mui/icons-material";
import { useAuth } from "../../../../auth";
import { getCreateStockingAreaMutationOptions } from "../../../../api/bpm/endpoints";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute("/__auth/admin/locations/create")({
  component: LocationCreatePage,
  staticData: {
    title: "Adicionar Área de lotação",
  },
});

const schema = z.object({
  name: z.string().min(1, "Nome é obrigatório"),
  description: z.string().min(1, "Descrição é obrigatória"),
});

type FormValues = z.infer<typeof schema>;

function LocationCreatePage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const tenant = user?.tenant || "default-domain";
  const queryClient = useQueryClient();
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error" | "info" | "warning";
  }>({
    open: false,
    message: "",
    severity: "info",
  });

  const handleCloseSnackbar = () => {
    setSnackbar({ open: false, message: "", severity: "info" });
  };

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: "",
      description: "",
    },
  });

  const mutation = useMutation({
    ...getCreateStockingAreaMutationOptions(),
    onSuccess: (response) => {
      setSnackbar({
        open: true,
        message: "Área de lotação criada com sucesso!",
        severity: "success",
      });
      queryClient.invalidateQueries({
        queryKey: ["listStockingarea"],
      });
      const newLocationId = (response as any).id;
      if (newLocationId) {
        navigate({ to: `/admin/locations/${newLocationId}` });
      } else {
        navigate({ to: "/admin/locations", search: { page: 1, size: 10 } });
      }
    },
    onError: (error) => {
      setSnackbar({
        open: true,
        message: "Erro ao criar área de lotação",
        severity: "error",
      });
      console.error(error);
    },
  });

  const onSubmit = (data: FormValues) => {
    mutation.mutate({ tenant, data: data });
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ display: "flex", alignItems: "center", mb: 4 }}>
        <Box
          sx={{
            width: 4,
            height: 40,
            bgcolor: "primary.main",
            borderRadius: 1,
            mr: 2,
          }}
        />
        <Typography variant="h5" fontWeight={600}>
          Adicionar Área de lotação
        </Typography>
      </Box>

      <form onSubmit={handleSubmit(onSubmit)}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>{t("admin.locations.sectionData")}</Typography>

        <Stack spacing={2.5} sx={{ mb: 4 }}>
          <Controller
            name="name"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label={t("common.name")}
                fullWidth
                size="small"
                error={!!errors.name}
                helperText={errors.name?.message}
              />
            )}
          />
          <Controller
            name="description"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label={t("admin.common.description")}
                fullWidth
                size="small"
                multiline
                rows={3}
                error={!!errors.description}
                helperText={errors.description?.message}
              />
            )}
          />
        </Stack>

        <Divider sx={{ mb: 3 }} />

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5 }}>
          <Button
            variant="outlined"
            onClick={() =>
              navigate({
                to: "/admin/locations",
                search: { page: 1, size: 10 },
              })
            }
            disabled={mutation.isPending}
          >{t("common.cancel").toUpperCase()}</Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            startIcon={
              mutation.isPending ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <SaveIcon />
              )
            }
            disabled={mutation.isPending}
          >{t("common.save").toUpperCase()}</Button>
        </Box>
      </form>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}
